import {NonEmptyString} from '@raison/types';
import {useMutation} from '@tanstack/react-query';
import {type as ark} from 'arktype';
import {array, partial, string, type} from 'io-ts';
import {UserStatusIoTs} from '~/entities/user';
import {expectEither, HttpClient, POST, ServerValidationResult, useHttpClient} from '~/library/network-facade';

export type AttachWalletsPayload = {
    password: string;
};

export const AttachWalletErrorCode = {
    passwordMismatch: 'password_mismatch',
    validationFailed: 'validation_failed',
} as const;

export function attachWallet(http: HttpClient, data: AttachWalletsPayload) {
    return http('v1/attach_wallet', POST(data)).then(
        ...expectEither({
            200: type({
                user: type({
                    username: NonEmptyString,
                    status: UserStatusIoTs,
                }),
            }),
            400: ServerValidationResult.createCodec(
                partial({
                    password: array(string),
                })
            ),
            422: ark({
                error_code: ark.enumerated(AttachWalletErrorCode.passwordMismatch),
            }),
        })
    );
}

export function useAttachWalletMutation() {
    const http = useHttpClient();
    return useMutation({
        mutationKey: ['v1/attach_wallet'],
        async mutationFn(data: AttachWalletsPayload) {
            const res = await attachWallet(http, data);

            switch (res.status) {
                case 200: {
                    return {value: res.json.user};
                }
                case 400: {
                    const issues = Object.fromEntries(Object.entries(res.json.fields ?? {}).map(([k, v]) => [k, v[0]]));
                    return {
                        problem: {
                            error_code: AttachWalletErrorCode.validationFailed,
                            issues,
                        },
                    };
                }
                case 422: {
                    return {problem: res.json};
                }
                case 'Abort': {
                    throw res.reason;
                }
            }
        },
    });
}
